import { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarDays, Clock4, UserCog, LogOut, Stethoscope, FileText, Trash2,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-slate-200 text-slate-700",
};

const SidebarLink = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
        isActive ? "bg-[#5E8B7E] text-white" : "text-slate-700 hover:bg-[#F4F7F6]"
      }`
    }
  >
    <Icon className="w-4 h-4" /> {label}
  </NavLink>
);

export default function ProfessionalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user || user.role !== "professional") {
    navigate("/professional/login", { replace: true });
    return null;
  }

  const doLogout = async () => {
    await logout();
    navigate("/professional/login", { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="pro-dashboard">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="bg-white rounded-2xl border border-[#EDF1EF] p-5 h-fit sticky top-24" data-testid="pro-sidebar">
          <div className="flex items-center gap-3 pb-4 border-b border-[#EDF1EF]">
            <span className="w-10 h-10 rounded-xl bg-[#D9E4E1] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-[#4A7065]" />
            </span>
            <div>
              <p className="font-heading text-sm text-slate-900 leading-tight">{user.name}</p>
              <p className="text-xs text-slate-500">{user.specialty}</p>
            </div>
          </div>
          <nav className="mt-4 space-y-1.5">
            <SidebarLink to="/professional/dashboard" icon={CalendarDays} label="Today" testId="pro-nav-today" />
            <SidebarLink to="/professional/dashboard/appointments" icon={CalendarDays} label="All appointments" testId="pro-nav-appts" />
            <SidebarLink to="/professional/dashboard/schedule" icon={Clock4} label="My schedule" testId="pro-nav-schedule" />
            <SidebarLink to="/professional/dashboard/profile" icon={UserCog} label="Profile" testId="pro-nav-profile" />
          </nav>
          <button
            data-testid="pro-logout-btn"
            onClick={doLogout}
            className="mt-6 w-full flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 px-3 py-2 rounded-xl hover:bg-red-50"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </aside>

        <main>
          <Routes>
            <Route index element={<ProTodayPanel />} />
            <Route path="appointments" element={<ProAppointmentsPanel />} />
            <Route path="schedule" element={<ProSchedulePanel />} />
            <Route path="profile" element={<ProProfilePanel />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

function ProTodayPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/professional/appointments", { params: { date: today() } });
      setItems(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <section data-testid="pro-today-panel">
      <h2 className="font-heading text-2xl text-slate-900">Today's appointments</h2>
      <p className="text-sm text-slate-500 mt-1">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</p>
      <ApptList items={items} loading={loading} onChange={refresh} />
    </section>
  );
}

function ProAppointmentsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/professional/appointments");
      setItems(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <section data-testid="pro-appts-panel">
      <h2 className="font-heading text-2xl text-slate-900">All appointments</h2>
      <ApptList items={items} loading={loading} onChange={refresh} showDate />
    </section>
  );
}

function ApptList({ items, loading, onChange, showDate }) {
  const [detailOn, setDetailOn] = useState(null);

  if (loading) return <p className="text-sm text-slate-400 mt-6">Loading…</p>;
  if (!items.length) return <p className="text-sm text-slate-400 mt-6" data-testid="pro-appt-empty">No appointments yet.</p>;
  return (
    <>
      <div className="mt-5 space-y-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`pro-appt-${a.id}`} className="bg-white rounded-2xl border border-[#EDF1EF] p-4 sm:p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-lg text-slate-900">{a.patient_name}</h3>
                  <Badge className={`${STATUS_COLORS[a.status] || "bg-slate-100 text-slate-700"} rounded-full text-xs`}>{a.status}</Badge>
                  {a.payment_status === "paid" && <Badge className="bg-green-100 text-green-700 rounded-full text-xs">Paid</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {showDate && <>{a.date} · </>}<b>{a.slot}</b> · {a.service_name}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{a.patient_phone} · {a.patient_email}</p>
                {a.notes && <p className="text-xs text-slate-600 mt-2 bg-[#F4F7F6] rounded-lg p-2.5">Patient note: {a.notes}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid={`pro-open-${a.id}`} size="sm" variant="outline" onClick={() => setDetailOn(a)} className="rounded-full">
                  <FileText className="w-3.5 h-3.5 mr-1" /> Open
                </Button>
                {a.status === "pending" && (
                  <Button data-testid={`pro-confirm-${a.id}`} size="sm" onClick={async () => {
                    await api.patch(`/professional/appointments/${a.id}`, { status: "confirmed" });
                    toast.success("Confirmed"); onChange();
                  }} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">Confirm</Button>
                )}
                {a.status === "confirmed" && (
                  <Button data-testid={`pro-complete-${a.id}`} size="sm" onClick={async () => {
                    await api.patch(`/professional/appointments/${a.id}`, { status: "completed" });
                    toast.success("Marked completed"); onChange();
                  }} className="rounded-full bg-[#2C557E] hover:bg-[#22436a]">Mark completed</Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {detailOn && (
        <AppointmentDetail
          appt={detailOn}
          onClose={() => { setDetailOn(null); onChange(); }}
        />
      )}
    </>
  );
}

function AppointmentDetail({ appt, onClose }) {
  const [diagnosis, setDiagnosis] = useState(appt.diagnosis || "");
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/professional/appointments/${appt.id}/shared-records`);
        setShared(data);
      } finally { setLoading(false); }
    })();
  }, [appt.id]);

  const saveDiagnosis = async () => {
    try {
      await api.patch(`/professional/appointments/${appt.id}/diagnosis`, { diagnosis });
      toast.success("Diagnosis saved");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="pro-appt-dialog">
        <DialogHeader>
          <DialogTitle>{appt.patient_name} — {appt.date} at {appt.slot}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="bg-[#F4F7F6] rounded-xl p-4 space-y-1.5">
            <p><b className="text-slate-500">Service:</b> {appt.service_name}</p>
            <p><b className="text-slate-500">Mode:</b> Online consultation</p>
            <p><b className="text-slate-500">Patient note:</b> {appt.notes || "—"}</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Shared documents (chosen by patient)</Label>
            {loading ? (
              <p className="text-xs text-slate-400 mt-2">Loading…</p>
            ) : shared.length === 0 ? (
              <p className="text-xs text-slate-400 mt-2" data-testid="pro-shared-empty">Patient has not shared any documents for this appointment.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {shared.map((r) => (
                  <li key={r.id} className="flex items-center justify-between bg-white border border-[#EDF1EF] rounded-lg p-2.5">
                    <span className="text-sm">{r.title}</span>
                    <a
                      data-testid={`pro-shared-open-${r.id}`}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/records/file/${r.id}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-[#2C557E] hover:underline"
                    >
                      Open ↗
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <Label htmlFor="pro-diag" className="text-xs uppercase tracking-wide text-slate-500">Your diagnosis / notes</Label>
            <Textarea id="pro-diag" data-testid="pro-diagnosis-input" rows={5} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} className="mt-1.5 rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
          <Button data-testid="pro-save-diagnosis" onClick={saveDiagnosis} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">Save diagnosis</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ProSchedulePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/professional/me/availability");
      setRows(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addRow = () => setRows((r) => [...r, { weekday: 0, start_time: "10:00", end_time: "12:00" }]);
  const updateRow = (i, key, val) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/professional/me/availability", { schedule: rows });
      toast.success("Schedule saved");
      refresh();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <section data-testid="pro-schedule-panel">
      <h2 className="font-heading text-2xl text-slate-900">My weekly availability</h2>
      <p className="text-sm text-slate-500 mt-1">Patients can book in 15-min slots within these windows. Add multiple rows for split shifts (e.g. morning + evening).</p>

      <div className="mt-6 space-y-3">
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="pro-schedule-empty">No availability set yet.</p>
        ) : rows.map((r, i) => (
          <div key={i} data-testid={`pro-sched-row-${i}`} className="bg-white border border-[#EDF1EF] rounded-xl p-3 flex items-center gap-3 flex-wrap">
            <select data-testid={`pro-sched-day-${i}`} value={r.weekday} onChange={(e) => updateRow(i, "weekday", parseInt(e.target.value, 10))} className="rounded-lg border border-[#E5EAE8] px-3 py-2 text-sm">
              {WEEKDAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
            </select>
            <Input data-testid={`pro-sched-from-${i}`} value={r.start_time} onChange={(e) => updateRow(i, "start_time", e.target.value)} placeholder="HH:MM" className="w-24 rounded-lg" />
            <span className="text-slate-400">to</span>
            <Input data-testid={`pro-sched-to-${i}`} value={r.end_time} onChange={(e) => updateRow(i, "end_time", e.target.value)} placeholder="HH:MM" className="w-24 rounded-lg" />
            <button data-testid={`pro-sched-remove-${i}`} onClick={() => removeRow(i)} className="ml-auto text-slate-400 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex gap-3">
        <Button data-testid="pro-sched-add" variant="outline" onClick={addRow} className="rounded-full">+ Add window</Button>
        <Button data-testid="pro-sched-save" disabled={saving} onClick={save} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
          {saving ? "Saving…" : "Save schedule"}
        </Button>
      </div>
    </section>
  );
}

function ProProfilePanel() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    specialty: user?.specialty || "",
    qualification: user?.qualification || "",
    bio: user?.bio || "",
  });
  const fee = user?.consultation_fee || 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    try {
      await api.patch("/professional/me", form);
      toast.success("Profile updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <section data-testid="pro-profile-panel">
      <h2 className="font-heading text-2xl text-slate-900">Profile</h2>
      <p className="text-sm text-slate-500 mt-1">Update what patients see when picking a care provider.</p>

      <div className="mt-6 space-y-4 bg-white border border-[#EDF1EF] rounded-2xl p-5">
        <div>
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled className="mt-1.5 rounded-xl bg-slate-50" />
        </div>
        <div>
          <Label htmlFor="pro-prof-spec">Specialty</Label>
          <Input id="pro-prof-spec" data-testid="pro-profile-specialty" value={form.specialty} onChange={set("specialty")} className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="pro-prof-qual">Qualifications</Label>
          <Input id="pro-prof-qual" data-testid="pro-profile-qual" value={form.qualification} onChange={set("qualification")} className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="pro-prof-bio">Bio</Label>
          <Textarea id="pro-prof-bio" data-testid="pro-profile-bio" rows={4} value={form.bio} onChange={set("bio")} className="mt-1.5 rounded-xl" />
        </div>
        <div className="bg-[#F4F7F6] rounded-xl p-3.5 text-sm text-slate-600">
          <b>Consultation fee (set by clinic):</b> ₹{fee}<br />
          <span className="text-xs text-slate-500">If you need to change your fee, please contact the clinic.</span>
        </div>
        <Button data-testid="pro-profile-save" onClick={save} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">Save profile</Button>
      </div>
    </section>
  );
}
